import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dateToLocalDateString } from './weeklyReports';
import { priceTypingProcess, priceOnFinish } from '../../supplementary_components/priceInputDelegate';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import cloneDeep from 'lodash/cloneDeep';
import { addPopup, addNotification } from '../../supplementary_components/store';
import { Form, Wrapper, Header, Content, Footer } from '../../general_components/forms/Form';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import { MdEdit } from 'react-icons/md';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import listStyles from '../../general_components/content_management/list_view/listView.module.scss';
import reportStyles from './reports.module.scss';
import { userHasAccess, userBelongsToCorrectDepartment } from '../../supplementary_components/userDataDelegate';
import { ThreeDotsButton } from '../../general_components/content_management/reusableItems/buttons';
import {
    normalizeDataset,
    mergeById,
    parsePaymentDate,
    stripTime,
} from './expenses';

const MONTHLY_EXPENSE_GRID_COLUMNS = '15rem 0.6fr 0.6fr 2fr 3rem';
const INITIAL_RANGE_MONTHS = 12;

const MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

function monthNumberFromName(name) {
    const i = MONTH_NAMES.indexOf(String(name || '').trim());
    return i >= 0 ? i + 1 : null;
}

function normalizeExpenseCategoryRows(res) {
    const list = normalizeDataset(res);
    if (!Array.isArray(list) || list.length === 0) return [];
    return list
        .map((item) => {
            if (item == null) return null;
            if (typeof item === 'string') {
                const s = item.trim();
                return s ? { id: s, name: s, builtin: s, custom: true } : null;
            }
            const name = item.name != null ? String(item.name).trim() : '';
            const builtin =
                item.builtin != null
                    ? String(item.builtin).trim()
                    : item.monthlyBuiltin != null
                      ? String(item.monthlyBuiltin).trim()
                      : '';
            if (!name && !builtin) return null;
            return {
                id: item.id,
                name: name || builtin,
                builtin: builtin || name,
                custom: item.custom != null ? !!item.custom : !builtin,
            };
        })
        .filter(Boolean)
        .sort((a, b) => (a.name || a.builtin).localeCompare(b.name || b.builtin, undefined, { sensitivity: 'base' }));
}

function serializeExpenseCategoryForForm(option) {
    if (!option || typeof option !== 'object') return '';
    return JSON.stringify({
        id: option.id ?? null,
        name: String(option.name ?? option.builtin ?? ''),
        builtin: String(option.builtin ?? option.name ?? ''),
        custom: !!option.custom,
    });
}

/** Match stored expense.category to a row and return the same value as select options. */
function expenseCategoryToSelectValue(storedCategory, categoryRows) {
    const rows = Array.isArray(categoryRows) ? categoryRows : [];
    let row = null;
    if (storedCategory && typeof storedCategory === 'object') {
        row = rows.find(
            (r) =>
                (storedCategory.id != null && r.id === storedCategory.id) ||
                (storedCategory.builtin != null && String(storedCategory.builtin) === String(r.builtin)) ||
                (storedCategory.monthlyBuiltin != null &&
                    String(storedCategory.monthlyBuiltin) === String(r.builtin)),
        );
    } else if (storedCategory != null && storedCategory !== '') {
        const s = String(storedCategory).trim();
        row = rows.find(
            (r) => r.builtin === s || r.name === s || (r.id != null && String(r.id) === s),
        );
    }
    if (!row) return '';
    return serializeExpenseCategoryForForm(row);
}

function compareExpensesInList(a, b) {
    const c = String(b.paymentDate || '').localeCompare(String(a.paymentDate || ''));
    if (c !== 0) return c;
    return String(b.id ?? '').localeCompare(String(a.id ?? ''));
}

function extractExpenseFromApiSuccess(res) {
    if (res == null || typeof res !== 'object') return null;
    if (res.id != null && (res.paymentDate !== undefined || res.amount !== undefined)) return res;
    if (res.dataset != null) {
        const d = Array.isArray(res.dataset) ? res.dataset[0] : res.dataset;
        if (d && typeof d === 'object' && d.id != null) return d;
    }
    if (res.data != null && typeof res.data === 'object' && res.data.id != null) return res.data;
    if (res.expense != null && typeof res.expense === 'object') return res.expense;
    return null;
}

function mergeExpenseIntoList(prev, expense) {
    if (!expense || expense.id == null) return prev;
    const idStr = String(expense.id);
    const idx = prev.findIndex((e) => String(e.id) === idStr);
    if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], ...expense };
        return next.sort(compareExpensesInList);
    }
    return [...prev, expense].sort(compareExpensesInList);
}

function earliestPaymentDayAmong(expenses) {
    let min = null;
    for (const e of expenses || []) {
        const pd = parsePaymentDate(e.paymentDate);
        if (!pd) continue;
        if (!min || pd.getTime() < min.getTime()) min = pd;
    }
    return min;
}

function oldestLoadedCursorAfterFetch(requestedRangeStart, rowsReturned) {
    const fromData = earliestPaymentDayAmong(rowsReturned);
    if (requestedRangeStart == null) {
        if (fromData) return stripTime(fromData);
        return stripTime(new Date());
    }
    const requested = stripTime(requestedRangeStart);
    if (!fromData) return requested;
    return fromData.getTime() < requested.getTime() ? stripTime(fromData) : requested;
}

function groupExpensesByMonth(expenses) {
    const buckets = new Map();
    for (const ex of expenses) {
        const pd = parsePaymentDate(ex.paymentDate);
        if (!pd) continue;
        const key = `${pd.getFullYear()}-${pd.getMonth()}`;
        if (!buckets.has(key)) {
            buckets.set(key, {
                month: pd.getMonth(),
                year: pd.getFullYear(),
                expenses: [],
            });
        }
        buckets.get(key).expenses.push(ex);
    }
    const groups = [...buckets.values()].sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
    });
    for (const g of groups) {
        g.expenses.sort(compareExpensesInList);
        g.totalAmount = g.expenses.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
    }
    return groups;
}

function formatMonthGroupLabel(monthIndex, year, totalAmount) {
    const monthName = new Date(year, monthIndex, 1).toLocaleString(undefined, { month: 'long' });
    return `${monthName} ${year} — ${priceOnFinish(totalAmount, true)}`;
}

function nextCalendarMonthFromPaymentDate(paymentDate) {
    const pd = parsePaymentDate(paymentDate);
    if (!pd) return null;
    const next = new Date(pd.getFullYear(), pd.getMonth() + 1, 15);
    return { month: next.getMonth() + 1, year: next.getFullYear() };
}

function copyMonthlyExpenseToNextMonth(expense, onSuccess) {
    const categoryId = expense.category?.id;
    if (!categoryId) {
        addNotification({ message: 'Category is missing', type: 'alert' });
        return;
    }
    const next = nextCalendarMonthFromPaymentDate(expense.paymentDate);
    if (!next) {
        addNotification({ message: 'Could not determine next month', type: 'alert' });
        return;
    }
    sendRequest(
        'POST',
        API_ROOT + 'monthly-expenses/add',
        null,
        (res) => {
            addNotification({ message: 'Expense copied to next month', type: 'success' });
            onSuccess(res);
        },
        (e) => {
            addNotification({ message: e?.customError || 'Could not copy expense', type: 'alert' });
        },
        {
            categoryId,
            month: next.month,
            year: next.year,
            amount: expense.amount,
            method: expense.method ?? null,
            description: expense.description ?? null,
        },
    );
}

function amountsEqual(a, b) {
    const na = parseFloat(a);
    const nb = parseFloat(b);
    if (Number.isNaN(na) || Number.isNaN(nb)) return false;
    return Math.abs(na - nb) < 0.005;
}

function parseAmountInput(val) {
    const parsed = parseFloat(String(val ?? '').replace(/[^0-9.]/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
}

function MonthlyExpenseAmountCell({ expense, disabled, onAmountUpdated }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const inputRef = useRef(null);
    const originalAmountRef = useRef(expense.amount);

    useEffect(() => {
        originalAmountRef.current = expense.amount;
    }, [expense.amount]);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const startEdit = () => {
        if (disabled || editing) return;
        setDraft(priceOnFinish(expense.amount));
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setDraft('');
    };

    const commitEdit = () => {
        const originalAmount = originalAmountRef.current;
        const parsed = parseAmountInput(draft);
        if (parsed == null || amountsEqual(parsed, originalAmount)) {
            cancelEdit();
            return;
        }

        sendRequest(
            'PATCH',
            API_ROOT + `monthly-expenses/${encodeURIComponent(expense.id)}`,
            null,
            (res) => {
                const row = extractExpenseFromApiSuccess(res);
                if (row) {
                    onAmountUpdated(row);
                }
                cancelEdit();
            },
            () => {
                addNotification({
                    message: 'Amount was not changed, something went wrong.',
                    type: 'alert',
                });
                cancelEdit();
            },
            { amount: parsed },
        );
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                type="text"
                className={reportStyles.expenseInlineAmountInput}
                value={draft}
                autoComplete="off"
                onChange={(e) => setDraft(priceTypingProcess(e.target.value))}
                onBlur={commitEdit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEdit();
                    }
                }}
            />
        );
    }

    return (
        <span
            className={disabled ? undefined : reportStyles.expenseInlineAmountDisplay}
            onDoubleClick={startEdit}
        >
            {priceOnFinish(expense.amount)}
        </span>
    );
}

function buildMonthlyExpenseAddForm(categoryRows) {
    const conf = cloneDeep(MONTHLY_EXPENSE_ADD_FORM);
    const catCol = conf.columns.find((c) => c.name === 'category');
    if (catCol) catCol.dataset = Array.isArray(categoryRows) ? categoryRows : [];
    return conf;
}

function openEditMonthlyExpense(expense, addFormWithCategories, applyExpenseFromApi) {
    if (!expense?.id) return;
    const editForm = cloneDeep(addFormWithCategories);
    editForm.formTitle = 'Edit Monthly Expense';
    editForm.customSubmitButton = 'Save Expense';
    editForm.successMessage = 'Monthly expense was updated';
    editForm.customMethod = 'PATCH';
    editForm.addLink = API_ROOT + `monthly-expenses/${encodeURIComponent(expense.id)}`;

    const categoryColumn = editForm.columns.find((c) => c.name === 'category');
    if (categoryColumn) {
        categoryColumn.defaultValue = () =>
            expenseCategoryToSelectValue(expense.category, categoryColumn.dataset || []);
    }

    const paymentDate = parsePaymentDate(expense.paymentDate);
    const monthColumn = editForm.columns.find((c) => c.name === 'month');
    if (monthColumn) {
        monthColumn.defaultValue = () =>
            paymentDate ? MONTH_NAMES[paymentDate.getMonth()] : MONTH_NAMES[now.getMonth()];
    }
    const yearColumn = editForm.columns.find((c) => c.name === 'year');
    if (yearColumn) {
        yearColumn.defaultValue = () =>
            paymentDate ? String(paymentDate.getFullYear()) : String(now.getFullYear());
    }
    const amountColumn = editForm.columns.find((c) => c.name === 'amount');
    if (amountColumn) {
        amountColumn.defaultValue = () => priceOnFinish(expense.amount);
    }
    const methodColumn = editForm.columns.find((c) => c.name === 'method');
    if (methodColumn) {
        methodColumn.defaultValue = () => expense.method ?? '';
    }
    const descriptionColumn = editForm.columns.find((c) => c.name === 'description');
    if (descriptionColumn) {
        descriptionColumn.defaultValue = () => expense.description ?? '';
    }

    addPopup({
        content: Form,
        popupData: { configuration: editForm },
        postSuccessAction: (value) => {
            applyExpenseFromApi(value);
        },
        EZCloseAllowed: false,
    });
}

function ManageMonthlyExpenseCategoriesPopup({ closePopup, postSuccessAction }) {
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [newName, setNewName] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [adding, setAdding] = useState(false);

    const applyRows = useCallback((allRows) => {
        const customOnly = (allRows || []).filter((r) => r.custom && r.id != null);
        setRows(customOnly);
        const d = {};
        customOnly.forEach((r) => {
            d[r.id] = r.name;
        });
        setDrafts(d);
    }, []);

    const load = useCallback(() => {
        setLoading(true);
        sendRequest(
            'GET',
            API_ROOT + 'monthly-expense-categories',
            null,
            (res) => {
                applyRows(normalizeExpenseCategoryRows(res));
                setLoading(false);
            },
            () => {
                addNotification({ message: 'Could not load categories', type: 'alert' });
                setLoading(false);
            },
            null,
        );
    }, [applyRows]);

    useEffect(() => {
        load();
    }, [load]);

    const saveRename = (id) => {
        const name = String(drafts[id] ?? '').trim();
        const orig = rows.find((r) => r.id === id)?.name ?? '';
        if (!name) {
            addNotification({ message: 'Name cannot be empty', type: 'alert' });
            return;
        }
        if (name === orig) return;
        setBusyId(id);
        sendRequest(
            'PATCH',
            API_ROOT + `monthly-expense-categories/${encodeURIComponent(id)}`,
            null,
            () => {
                setBusyId(null);
                addNotification({ message: 'Category updated', type: 'success' });
                load();
                if (postSuccessAction) postSuccessAction();
            },
            () => {
                setBusyId(null);
                addNotification({ message: 'Could not update category', type: 'alert' });
            },
            { name },
        );
    };

    const addNew = () => {
        const name = newName.trim();
        if (!name) {
            addNotification({ message: 'Enter a category name', type: 'alert' });
            return;
        }
        setAdding(true);
        sendRequest(
            'POST',
            API_ROOT + 'monthly-expense-categories',
            null,
            () => {
                setAdding(false);
                setNewName('');
                addNotification({ message: 'Category added', type: 'success' });
                load();
                if (postSuccessAction) postSuccessAction();
            },
            () => {
                setAdding(false);
                addNotification({ message: 'Could not add category', type: 'alert' });
            },
            { name },
        );
    };

    return (
        <Wrapper>
            <Header><span>Manage monthly expense categories</span></Header>
            <Content className={reportStyles.manageCategoriesContent}>
                <p></p>
                {loading ? (
                    <LoadingIcon state={true} height="6rem" />
                ) : (
                    <>
                        {rows.length === 0 ? (
                            <p className={reportStyles.manageCategoriesEmpty}>No custom categories yet.</p>
                        ) : (
                            <ul className={reportStyles.manageCategoriesList}>
                                {rows.map((row) => (
                                    <li key={row.id} className={reportStyles.manageCategoriesRow}>
                                        <input
                                            type="text"
                                            className={reportStyles.manageCategoriesInput}
                                            value={drafts[row.id] ?? ''}
                                            onChange={(e) => setDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                            disabled={busyId === row.id}
                                        />
                                        <button
                                            type="button"
                                            className={reportStyles.manageCategoriesSaveBtn}
                                            disabled={busyId === row.id || adding}
                                            onClick={() => saveRename(row.id)}
                                        >
                                            {busyId === row.id ? 'Saving…' : 'Save'}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className={reportStyles.manageCategoriesAddRow}>
                            <input
                                type="text"
                                className={reportStyles.manageCategoriesInput}
                                placeholder="New category name"
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                disabled={adding}
                            />
                            <button
                                type="button"
                                className={reportStyles.manageCategoriesPrimaryBtn}
                                disabled={adding}
                                onClick={addNew}
                            >
                                {adding ? 'Adding…' : 'Add'}
                            </button>
                        </div>
                    </>
                )}
            </Content>
            <Footer>
                <div onClick={closePopup}>Close</div>
            </Footer>
        </Wrapper>
    );
}

function monthlyExpenseRemoveFromListing(expense, postSuccessAction) {
    addPopup({
        content: ConfirmationDialogue,
        popupData: {
            confirmationDialogue: true,
            action: (postResultAction) => {
                sendRequest(
                    'DELETE',
                    API_ROOT + `monthly-expenses/${expense.id}`,
                    null,
                    () => {
                        postSuccessAction();
                        postResultAction(true);
                    },
                    () => {
                        addNotification({ message: 'Something Went Wrong', type: 'alert' });
                        postResultAction(true);
                    },
                );
            },
            cancelAction: (closePopup) => {
                closePopup();
            },
            name: 'Confirm',
            formTitle: 'Delete Expense',
            submitTitle: 'Delete',
            message: 'Are you sure you want to delete this expense?',
        },
        postSuccessAction: () => {},
        EZCloseAllowed: false,
    });
}

const now = new Date();

export const MONTHLY_EXPENSE_ADD_FORM = {
    formAccessLevel: { key: 'MONTHLY_EXPENSES', level: 'MANAGE' },
    formTitle: 'Add Monthly Expense',
    name: 'addMonthlyExpenseForm',
    addLink: API_ROOT + 'monthly-expenses/add',
    successMessage: 'Monthly expense was added',
    buildRequestBody: (data) => {
        const category =
            data.category && typeof data.category === 'object'
                ? data.category
                : (() => {
                      try {
                          return JSON.parse(data.category);
                      } catch {
                          return null;
                      }
                  })();
        return {
            categoryId: category?.id ?? null,
            month: data.month,
            year: data.year,
            amount: data.amount,
            method: data.method,
            description: data.description,
        };
    },
    columns: [
        {
            label: 'Category',
            autoComplete: 'off',
            name: 'category',
            type: 'select',
            mandatory: true,
            dataset: [],
            selectOptionValue: (option) => serializeExpenseCategoryForForm(option),
            processBeforeSending: (val) => {
                if (val == null || val === '') return null;
                try {
                    return JSON.parse(val);
                } catch {
                    return null;
                }
            },
        },
        {
            label: 'Month',
            name: 'month',
            mandatory: true,
            type: 'select',
            autoComplete: 'off',
            dataset: MONTH_NAMES,
            defaultValue: () => MONTH_NAMES[now.getMonth()],
            processBeforeSending: (val) => monthNumberFromName(val),
            checks: [
                (value) => {
                    if (!monthNumberFromName(value)) return 'Select a month';
                    return null;
                },
            ],
        },
        {
            label: 'Year',
            name: 'year',
            mandatory: true,
            autoComplete: 'off',
            defaultValue: () => String(now.getFullYear()),
            checks: [
                (value) => {
                    const y = parseInt(String(value).trim(), 10);
                    if (!y || y < 1900 || y > 9999) return 'Enter a valid year';
                    return null;
                },
            ],
            processBeforeSending: (val) => parseInt(String(val).trim(), 10),
        },
        {
            label: 'Amount',
            name: 'amount',
            mandatory: true,
            placeholder: '$0.00',
            autoComplete: 'off',
            filters: [priceTypingProcess],
            onblur: (val) => priceOnFinish(val),
            processBeforeSending: (val) => parseFloat(val.replace(/[^0-9.]/g, '')),
        },
        {
            label: 'Payment Method',
            autoComplete: 'off',
            name: 'method',
            type: 'select',
            dataset: ['', 'EFS', 'WIRE', 'CREDIT_CARD', 'CASH', 'ZELLE'],
            processBeforeSending: (val) => (val === '' ? null : val),
        },
        {
            label: 'Description',
            name: 'description',
            type: 'textArea',
            rowCount: 5,
        },
    ],
};

const MONTHLY_EXPENSE_LISTING_CONF = {
    getRequestLink: API_ROOT + 'monthly-expenses',
    columnProportions: MONTHLY_EXPENSE_GRID_COLUMNS,
    dataTitle: 'Monthly Expense',
    dataTitlePlural: 'Monthly Expenses',
    addButton: true,
    addConf: MONTHLY_EXPENSE_ADD_FORM,
    editAccessLevel: { key: 'MONTHLY_EXPENSES', level: 'MANAGE' },
    specialActions: [
        {
            requiredPermission: { key: 'MONTHLY_EXPENSES', level: 'MANAGE' },
            buttonLabel: 'Manage Categories',
            icon: <MdEdit />,
            onClick: (requestReload) => {
                addPopup({
                    content: ManageMonthlyExpenseCategoriesPopup,
                    popupData: {},
                    postSuccessAction: requestReload,
                    EZCloseAllowed: true,
                    customMinWidth: '28rem',
                });
            },
        },
    ],
    columns: [
        { label: 'Category', columnName: 'category' },
        {
            label: 'Amount',
            columnName: 'amount',
        },
        {
            label: 'Payment Method',
            columnName: 'method',
            valueInterpreter: (data) => data || '-',
        },
        {
            label: 'Description',
            columnName: 'description',
            valueInterpreter: (data) => data || '-',
        },
        {
            actionButtons: true,
            onRemoveAction: monthlyExpenseRemoveFromListing,
        },
    ],
};

const DISPLAY_COLUMNS = MONTHLY_EXPENSE_LISTING_CONF.columns.filter((c) => !c.actionButtons);

export const MonthlyExpensesPage = ({ companyData: _companyData }) => {
    const currentUserDepartment = localStorage.getItem('department');

    const [customCategories, setCustomCategories] = useState([]);
    const [allExpenses, setAllExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadMoreBusy, setLoadMoreBusy] = useState(false);
    const [error, setError] = useState(null);
    const [hasMoreOlder, setHasMoreOlder] = useState(true);
    const [oldestLoadedDay, setOldestLoadedDay] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);

    const requestReload = useCallback(() => {
        setReloadTick((t) => t + 1);
    }, []);

    const applyExpenseFromApi = useCallback((apiValue) => {
        const row = extractExpenseFromApiSuccess(apiValue);
        if (row) {
            setAllExpenses((prev) => mergeExpenseIntoList(prev, row));
        } else {
            addNotification({
                message: 'Expense was saved but the response could not be merged into the list.',
                type: 'alert',
            });
        }
    }, []);

    useEffect(() => {
        const ac = new AbortController();
        sendRequest(
            'GET',
            API_ROOT + 'monthly-expense-categories',
            ac.signal,
            (res) => {
                setCustomCategories(normalizeExpenseCategoryRows(res));
            },
            () => setCustomCategories([]),
            null,
        );
        return () => ac.abort();
    }, [reloadTick]);

    const userCanEdit = userHasAccess(MONTHLY_EXPENSE_LISTING_CONF.editAccessLevel);

    const displayColumnsForTable = useMemo(
        () =>
            DISPLAY_COLUMNS.map((col) => {
                if (col.columnName === 'category') {
                    return {
                        ...col,
                        valueInterpreter: (val) => val?.name ?? val?.builtin ?? '-',
                    };
                }
                if (col.columnName === 'amount') {
                    return {
                        ...col,
                        customComponent: (row) => (
                            <MonthlyExpenseAmountCell
                                key={`amount-${row.id}`}
                                expense={row}
                                disabled={!userCanEdit}
                                onAmountUpdated={(updated) => {
                                    setAllExpenses((prev) => mergeExpenseIntoList(prev, updated));
                                }}
                            />
                        ),
                    };
                }
                return col;
            }),
        [userCanEdit],
    );

    const addFormWithCategories = useMemo(
        () => buildMonthlyExpenseAddForm(customCategories),
        [customCategories],
    );

    const openEditExpense = useCallback(
        (expense) => {
            openEditMonthlyExpense(expense, addFormWithCategories, applyExpenseFromApi);
        },
        [addFormWithCategories, applyExpenseFromApi],
    );

    const fetchRange = useCallback((rangeStart, rangeEnd, signal) => {
        const noDateBounds = rangeStart == null || rangeEnd == null;
        const qs = noDateBounds
            ? '?pageNumber=0'
            : `?startDate=${dateToLocalDateString(stripTime(rangeStart))}&endDate=${dateToLocalDateString(
                  stripTime(rangeEnd),
              )}&pageNumber=0`;
        return new Promise((resolve) => {
            const normalize = (res) => normalizeDataset(res);
            sendRequest(
                'GET',
                API_ROOT + 'monthly-expenses' + qs,
                signal,
                (res) => resolve(normalize(res)),
                () => {
                    sendRequest(
                        'GET',
                        API_ROOT + 'monthly-expenses?pageNumber=0',
                        signal,
                        (res2) => resolve(normalize(res2)),
                        () => resolve([]),
                        null,
                    );
                },
                null,
            );
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        const ac = new AbortController();

        const run = async () => {
            setLoading(true);
            setError(null);
            try {
                const rows = await fetchRange(null, null, ac.signal);
                if (cancelled) return;
                setAllExpenses(rows);
                setOldestLoadedDay(oldestLoadedCursorAfterFetch(null, rows));
                setHasMoreOlder(true);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [fetchRange]);

    const monthGroups = useMemo(() => groupExpensesByMonth(allExpenses), [allExpenses]);

    const onLoadMore = useCallback(async () => {
        if (!hasMoreOlder || loadMoreBusy || !oldestLoadedDay || loading) return;
        setLoadMoreBusy(true);
        const ac = new AbortController();
        const chunkEnd = new Date(oldestLoadedDay);
        chunkEnd.setDate(chunkEnd.getDate() - 1);
        const chunkStart = new Date(chunkEnd);
        chunkStart.setMonth(chunkStart.getMonth() - (INITIAL_RANGE_MONTHS - 1));
        chunkStart.setDate(1);
        try {
            const rows = await fetchRange(chunkStart, chunkEnd, ac.signal);
            setAllExpenses((prev) => {
                const { merged, addedCount } = mergeById(prev, rows);
                if (rows.length === 0 || addedCount === 0) {
                    setHasMoreOlder(false);
                } else {
                    setOldestLoadedDay(oldestLoadedCursorAfterFetch(chunkStart, rows));
                }
                return merged;
            });
        } finally {
            setLoadMoreBusy(false);
        }
    }, [fetchRange, hasMoreOlder, loadMoreBusy, oldestLoadedDay, loading]);

    if (error) {
        return <div className="errorMessage">{error.customError ? error.customError : 'Something went wrong'}</div>;
    }

    if (loading) {
        return <LoadingIcon state={true} height="10rem" />;
    }

    return (
        <div className={reportStyles.mainContainer}>
            <div className={listStyles.pageHeader}>
                <div>{MONTHLY_EXPENSE_LISTING_CONF.dataTitlePlural}</div>
                <div className={listStyles.actionsContainer}>
                    {MONTHLY_EXPENSE_LISTING_CONF.specialActions
                        ? MONTHLY_EXPENSE_LISTING_CONF.specialActions.map((action, index) => {
                              if (action.requiredPermission
                                    ? !userHasAccess(action.requiredPermission)
                                    : (action.requiredDepartment && currentUserDepartment !== action.requiredDepartment)) return null;
                              return (
                                  <div key={index} className={`${listStyles.addButton} non-selectable`}>
                                      <div onClick={() => action.onClick(requestReload)}>
                                          {action.buttonLabel}
                                          <div className="actionIcon">{action.icon}</div>
                                      </div>
                                  </div>
                              );
                          })
                        : null}
                    <div>
                        {MONTHLY_EXPENSE_LISTING_CONF.addButton &&
                        userHasAccess(MONTHLY_EXPENSE_LISTING_CONF.addConf.formAccessLevel) &&
                        (!MONTHLY_EXPENSE_LISTING_CONF.addConf.formAccessDepartment ||
                            userBelongsToCorrectDepartment(
                                MONTHLY_EXPENSE_LISTING_CONF.addConf.formAccessDepartment,
                            )) ? (
                            <div
                                className={`${listStyles.addButton} non-selectable`}
                                onClick={() =>
                                    addPopup({
                                        content: Form,
                                        popupData: { configuration: addFormWithCategories },
                                        postSuccessAction: (value) => {
                                            applyExpenseFromApi(value);
                                        },
                                        EZCloseAllowed: addFormWithCategories.EZCloseAllowed ?? true,
                                    })
                                }
                            >
                                Add {MONTHLY_EXPENSE_LISTING_CONF.dataTitle} +
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            <div
                className={listStyles.container}
                // eslint-disable-next-line
                style={{ ['--list-columns']: MONTHLY_EXPENSE_GRID_COLUMNS }}
            >
                <div className={listStyles.listViewHeaderWrapper} style={{ gridTemplateColumns: 'var(--list-columns)' }}>
                    {displayColumnsForTable.map((col) => (
                        <div key={col.columnName} className={listStyles.listHeaderLabel}>
                            <span className={listStyles.labelText}>{col.label}</span>
                        </div>
                    ))}
                    <div className={listStyles.listHeaderLabel} />
                </div>

                {monthGroups.length === 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                        <div className={reportStyles.expensesEmptyBanner} style={{ gridColumn: '1 / -1' }}>
                            No {MONTHLY_EXPENSE_LISTING_CONF.dataTitlePlural.toLowerCase()} in the loaded date range. Use
                            “Load more” to fetch older periods.
                        </div>
                    </div>
                ) : (
                    monthGroups.map((group) => (
                        <React.Fragment key={`${group.year}-${group.month}`}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'var(--list-columns)', width: '100%' }}>
                                <div className={reportStyles.expensesWeekDividerInner} style={{ gridColumn: '1 / -1' }}>
                                    {formatMonthGroupLabel(group.month, group.year, group.totalAmount)}
                                </div>
                            </div>
                            {group.expenses.map((expense) => (
                                <div
                                    key={expense.id ?? `${expense.paymentDate}-${expense.amount}`}
                                    className={listStyles.row}
                                >
                                    {displayColumnsForTable.map((col) => (
                                        <div key={col.columnName}>
                                            {col.customComponent ? (
                                                col.customComponent(expense)
                                            ) : (
                                                <span>
                                                    {col.valueInterpreter
                                                        ? col.valueInterpreter(expense[col.columnName])
                                                        : expense[col.columnName]}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    <ThreeDotsButton
                                        disabled={!userCanEdit}
                                        position="relative"
                                        hideRemove={false}
                                        customButton={{
                                            icon: <HiOutlineDocumentDuplicate />,
                                            action: () =>
                                                copyMonthlyExpenseToNextMonth(expense, applyExpenseFromApi),
                                        }}
                                        onEdit={() => openEditExpense(expense)}
                                        onRemove={() =>
                                            monthlyExpenseRemoveFromListing(expense, () => {
                                                setAllExpenses((prev) =>
                                                    prev.filter((e) => String(e.id) !== String(expense.id)),
                                                );
                                            })
                                        }
                                    />
                                </div>
                            ))}
                        </React.Fragment>
                    ))
                )}
            </div>

            {hasMoreOlder ? (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div
                        className={loadMoreBusy ? 'loading' : ''}
                        onClick={() => {
                            if (!loadMoreBusy) onLoadMore();
                        }}
                    >
                        {loadMoreBusy ? 'Loading…' : 'Load more'}
                    </div>
                </div>
            ) : (
                <div className={listStyles.loadMoreButtonWrapper}>
                    <div className={listStyles.noMoreToload}>No older expenses to load</div>
                </div>
            )}
        </div>
    );
};
